import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { FileDropzone } from "src/ui/shared/form/FileDropzone";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useCreatePolicyMutation } from "@dashboard/policies/api";
import { policyNameExists } from "@dashboard/policies/PoliciesPage.utils";
import { getPolicyRuleLabels, PolicyCommandLabels } from "@dashboard/shared/components/PolicyCommands/policyCommandDisplay";
import { MdmPolicyCommand } from "@devices/types";
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { UncontrolledTextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./ImportPolicyDialog.style";

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: (policyId: string) => void;
    policies: API.PolicyViewModel[];
}

type PreviewEntryData = {
    payload: MdmPolicyCommand | null;
    commandName: string | null;
};

type PreviewEntry = PreviewEntryData & {
    labels: PolicyCommandLabels;
};

type ParseErrorKey = "invalidJson" | "notAnArray" | "emptyFile";

type ParseState =
    | { status: "idle" }
    | { status: "error"; errorKey: ParseErrorKey }
    | { status: "ready"; entries: PreviewEntryData[]; policyName: string | null };

function getPolicyNameFromFile(fileName: string): string {
    return fileName.replace(/\.[^./]+$/, "");
}

function readFileContents(file: File): Promise<string | null> {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = (event) => {
            resolve(event.target?.result?.toString() ?? null);
        };
        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsText(file);
    });
}

function toPreviewEntry(item: unknown): PreviewEntryData {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
        return { payload: null, commandName: null };
    }

    const record = item as Record<string, unknown>;
    if (typeof record.command !== "string" || !record.command.trim()) {
        return { payload: null, commandName: null };
    }

    return { payload: item as MdmPolicyCommand, commandName: record.command };
}

function parseFileContents(contents: string): ParseState {
    let parsed: unknown;

    try {
        parsed = JSON.parse(contents);
    } catch (_error) {
        return { status: "error", errorKey: "invalidJson" };
    }

    let policyName: string | null = null;
    let commands: unknown = parsed;

    if (!Array.isArray(parsed) && parsed !== null && typeof parsed === "object") {
        const record = parsed as Record<string, unknown>;
        commands = record.content;
        policyName = typeof record.name === "string" && record.name.trim() ? record.name : null;
    }

    if (!Array.isArray(commands)) {
        return { status: "error", errorKey: "notAnArray" };
    }
    if (!commands.length) {
        return { status: "error", errorKey: "emptyFile" };
    }

    return { status: "ready", entries: commands.map(toPreviewEntry), policyName };
}

const EntryStatusIcon = ({ entry }: { entry: PreviewEntryData & { labels: PolicyCommandLabels } }) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    if (!entry.commandName) {
        const label = t("modals:importPolicy.preview.invalidEntry");
        return (
            <Tooltip title={label}>
                <i className={`fa-solid fa-circle-exclamation ${classes.statusIconError}`} role="img" aria-label={label} />
            </Tooltip>
        );
    }
    if (!entry.labels.recognized) {
        const label = t("modals:importPolicy.preview.unrecognizedRule");
        return (
            <Tooltip title={label}>
                <i className={`fa-solid fa-triangle-exclamation ${classes.statusIconWarning}`} role="img" aria-label={label} />
            </Tooltip>
        );
    }
    const label = t("modals:importPolicy.preview.validEntry");
    return <i className={`fa-solid fa-check ${classes.statusIconSuccess}`} role="img" aria-label={label} />;
};

const ParseErrorAlert = ({ errorKey }: { errorKey: ParseErrorKey }) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    // Static literal keys so the i18next extractor keeps them
    const messages: Record<ParseErrorKey, string> = {
        invalidJson: t("modals:importPolicy.error.invalidJson"),
        notAnArray: t("modals:importPolicy.error.notAnArray"),
        emptyFile: t("modals:importPolicy.error.emptyFile"),
    };

    return (
        <CustomAlert severity="error" className={classes.parseErrorAlert}>
            {messages[errorKey]}
        </CustomAlert>
    );
};

const PolicyImportPreviewItem = ({ entry, divider }: { entry: PreviewEntry; divider: boolean }) => {
    const { classes } = useStyles();

    return (
        <ListItem divider={divider}>
            <ListItemText primary={entry.labels.primary} secondary={entry.labels.secondary} />
            <ListItemIcon className={classes.statusIconWrapper}>
                <EntryStatusIcon entry={entry} />
            </ListItemIcon>
        </ListItem>
    );
};

export const ImportPolicyDialog = ({ open, onClose, onCreated, policies }: Props) => {
    const { t, i18n } = useTranslation(["modals", "common"]);
    const { classes } = useStyles();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const [createPolicy, { isLoading }] = useCreatePolicyMutation();

    const [selectedFileName, setSelectedFileName] = React.useState<string | null>(null);
    const [parseState, setParseState] = React.useState<ParseState>({ status: "idle" });
    const [policyName, setPolicyName] = React.useState("");

    React.useEffect(() => {
        // Remount-equivalent reset on every open so a previously selected file does not leak into the next import
        if (open) {
            setParseState({ status: "idle" });
            setSelectedFileName(null);
            setPolicyName("");
        }
    }, [open]);

    const handleFileSelected = React.useCallback(
        async (files: File[]) => {
            const file = files[0];
            if (!file) {
                return;
            }

            setSelectedFileName(file.name);

            let fileContents: string | null = null;
            try {
                fileContents = await readFileContents(file);
            } catch (error: unknown) {
                if (error instanceof Error) {
                    showErrorToast(error.message);
                } else {
                    showErrorToast(t("modals:importPolicy.toast.pleaseTryAgain"));
                }
                setParseState({ status: "idle" });
                return;
            }

            if (fileContents === null) {
                showErrorToast(t("modals:importPolicy.toast.pleaseTryAgain"));
                setParseState({ status: "idle" });
                return;
            }

            const state = parseFileContents(fileContents);
            setParseState(state);
            setPolicyName(state.status === "ready" ? (state.policyName ?? getPolicyNameFromFile(file.name)) : "");
        },
        [t],
    );

    const previewEntries = React.useMemo((): PreviewEntry[] => {
        if (parseState.status !== "ready") {
            return [];
        }

        return parseState.entries.map((entry) => ({
            ...entry,
            labels: entry.payload ? getPolicyRuleLabels(entry.payload) : getPolicyRuleLabels({} as MdmPolicyCommand),
        }));
    }, [parseState, i18n.language]);

    const importableCommands =
        parseState.status === "ready" ? parseState.entries.flatMap((entry) => (entry.payload ? [entry.payload] : [])) : [];

    const hasNoImportableCommands = parseState.status === "ready" && !importableCommands.length;

    const trimmedName = policyName.trim();
    const nameError = !trimmedName
        ? t("modals:createPolicy.input.name.validation.required")
        : policyNameExists(policies, trimmedName)
          ? t("modals:createPolicy.input.name.validation.exists")
          : null;

    const onSubmit = async () => {
        if (!importableCommands.length || !selectedFileName || workspaceId === null || nameError) {
            return;
        }

        try {
            const result = await createPolicy({
                workspaceId,
                name: trimmedName,
                content: importableCommands,
            }).unwrap();
            showSuccessToast(t("modals:createPolicy.toast.createSuccess"));
            onCreated(result.model.id);
            onClose();
        } catch {
            // The policies API's transformErrorResponse already shows an error toast for the
            // failed request, so the modal is simply left open here for the user to retry.
        }
    };

    return (
        <Modal
            title={t("modals:importPolicy.title")}
            open={open}
            onClose={onClose}
            description={t("modals:importPolicy.description")}
            endButton={
                <PrimaryButton
                    onClick={onSubmit}
                    disabled={isLoading || !importableCommands.length || !!nameError}
                    loading={isLoading}
                >
                    {t("common:button.import")}
                </PrimaryButton>
            }
        >
            <FileDropzone
                inputId="import-policy-file"
                accept=".json,application/json"
                label={<Trans ns="modals" i18nKey="importPolicy.input.file.dropzone" components={{ u: <u /> }} />}
                selectedFileName={selectedFileName ?? undefined}
                onFilesSelected={handleFileSelected}
                className={classes.dropzone}
            />

            {parseState.status === "error" ? <ParseErrorAlert errorKey={parseState.errorKey} /> : null}

            {parseState.status === "ready" ? (
                <Box className={classes.previewContainer}>
                    <UncontrolledTextField
                        label={t("common:label.name")}
                        value={policyName}
                        onChange={(event) => setPolicyName(event.target.value)}
                        required
                        fullWidth
                        errorMessage={nameError ?? undefined}
                    />
                    <Typography variant="subtitle2" className={classes.commandsFound}>
                        {t("modals:importPolicy.preview.rulesFound", { count: parseState.entries.length })}
                    </Typography>
                    {hasNoImportableCommands ? (
                        <CustomAlert severity="warning" className={classes.noImportableAlert}>
                            {t("modals:importPolicy.preview.noImportableRules")}
                        </CustomAlert>
                    ) : null}
                    <List dense className={classes.previewList}>
                        {previewEntries.map((entry, index) => (
                            <PolicyImportPreviewItem
                                key={`${index}-${entry.commandName ?? "invalid"}`}
                                entry={entry}
                                divider={index < previewEntries.length - 1}
                            />
                        ))}
                    </List>
                </Box>
            ) : null}
        </Modal>
    );
};
